/**
 * Newsletter regeneration helpers — extracted from NewsletterAIService.js
 * as part of #263 (the #229 decomposition sequence). Closed out across
 * Wave 1 (#271) and Wave 2 (this PR).
 *
 * This module holds functions that the NewsletterAIService class methods
 * delegate to. They take the service instance as their first argument so
 * they can reuse the class's apiEndpoint / logger / auth / budget plumbing
 * without dragging that state into the module.
 *
 * Public exports:
 *   - buildStyleInstructions(styleProfile) — pure helper (no service arg).
 *     Turns a writing-style profile into a prose block that the LLM uses
 *     as part of its system prompt.
 *   - rewriteSelection(service, text, action, options) — service-bound
 *     helper for the in-line rewrite toolbar (rewrite / shorter / more_formal
 *     / more_casual / simplify).
 *   - regenerateWithStyle(service, content, styleProfile, options) —
 *     service-bound "Apply My Style" rewrite of an entire piece of content.
 *   - regenerateSection(service, params) — service-bound per-section
 *     regenerator that pulls fresh S3 placeholders (Rule 17) and polishes
 *     only the prose, leaving facts intact.
 */

import lambdaPlaceholderService from '../LambdaPlaceholderService.js';
import editorialBriefService from '../EditorialBriefService.js';
import {
  createNetworkError,
  createValidationError,
} from '../../utils/StandardErrorFormat.js';

/**
 * Sections that may carry an EditorialBrief thesis (#313 multi-section
 * unlock — extends the #170 alpha proof which gated on `chart_of_the_week`
 * only). Keys match the placeholder-category prefixes in
 * LambdaPlaceholderService, plus the editorial-only `chart_of_the_week`
 * concept (no placeholder catalog entries — owned entirely by the brief).
 *
 * Frozen so accidental mutation at runtime is caught immediately rather
 * than silently expanding the brief surface area.
 */
export const BRIEF_AWARE_SECTIONS = Object.freeze([
  'market_overview',
  'chart_of_the_week',
  'sector_analysis',
  'economic_indicators',
  'investment_insights',
  'market_news',
  'market_sentiment',
  'technical_analysis',
  'business_insights',
  'tax_reminders',
]);

/**
 * Build the writing-style instruction block. Pure function — no service deps.
 */
export function buildStyleInstructions(styleProfile) {
  const instructions = [];

  // Writing style instructions
  if (styleProfile.writingStyle === 'formal') {
    instructions.push('WRITING STYLE: Formal and professional');
    instructions.push('- Use complete sentences and proper grammar');
    instructions.push('- Avoid contractions (use "do not" instead of "don\'t")');
    instructions.push('- Use sophisticated vocabulary');
    instructions.push('- Maintain professional distance');
  } else if (styleProfile.writingStyle === 'conversational') {
    instructions.push('WRITING STYLE: Conversational and approachable');
    instructions.push('- Use contractions naturally (it\'s, we\'re, you\'ll)');
    instructions.push('- Write as if speaking directly to the reader');
    instructions.push('- Use simpler, everyday language');
    instructions.push('- Include personal pronouns (you, we)');
  } else if (styleProfile.writingStyle === 'technical') {
    instructions.push('WRITING STYLE: Technical and analytical');
    instructions.push('- Use precise financial terminology');
    instructions.push('- Include data-driven language');
    instructions.push('- Maintain objective tone');
    instructions.push('- Focus on metrics and specifics');
  }

  // Tone instructions
  if (styleProfile.tone === 'friendly') {
    instructions.push('\nTONE: Warm and friendly');
    instructions.push('- Be personable and engaging');
    instructions.push('- Show empathy and understanding');
    instructions.push('- Use encouraging language');
  } else if (styleProfile.tone === 'professional') {
    instructions.push('\nTONE: Professional and balanced');
    instructions.push('- Maintain credibility and expertise');
    instructions.push('- Be clear and direct');
    instructions.push('- Stay objective and factual');
  } else if (styleProfile.tone === 'authoritative') {
    instructions.push('\nTONE: Authoritative and confident');
    instructions.push('- Project expertise and confidence');
    instructions.push('- Use definitive language');
    instructions.push('- Avoid hedging or uncertainty');
  }

  // Complexity instructions
  if (styleProfile.complexity === 'beginner') {
    instructions.push('\nCOMPLEXITY: Beginner-friendly');
    instructions.push('- Explain financial concepts in simple terms');
    instructions.push('- Avoid jargon or explain it when used');
    instructions.push('- Use analogies and examples');
    instructions.push('- Break down complex ideas');
  } else if (styleProfile.complexity === 'intermediate') {
    instructions.push('\nCOMPLEXITY: Intermediate level');
    instructions.push('- Use standard financial terminology');
    instructions.push('- Assume basic financial literacy');
    instructions.push('- Brief explanations when needed');
  } else if (styleProfile.complexity === 'advanced') {
    instructions.push('\nCOMPLEXITY: Advanced / Expert level');
    instructions.push('- Use sophisticated financial concepts');
    instructions.push('- Assume high financial literacy');
    instructions.push('- Include technical details');
    instructions.push('- No need to explain common terms');
  }

  // Sentence length
  if (styleProfile.sentenceLength === 'short') {
    instructions.push('\nSENTENCE LENGTH: Short and punchy (10-15 words)');
  } else if (styleProfile.sentenceLength === 'long') {
    instructions.push('\nSENTENCE LENGTH: Longer and detailed (20-30 words)');
  } else {
    instructions.push('\nSENTENCE LENGTH: Medium (15-20 words)');
  }

  return instructions.join('\n');
}

/**
 * Rewrite a selected passage of newsletter text via one of the inline
 * AI toolbar actions. Service-bound (uses the caller's apiEndpoint +
 * auth + logger + budget plumbing).
 */
export async function rewriteSelection(service, text, action, { style } = {}) {
  await service._assertWithinDailyBudget();
  const trimmed = (text || '').toString().trim();
  if (!trimmed) {
    throw createValidationError('Selected text is required for rewriteSelection().');
  }

  const actionInstructions = {
    rewrite:    'Rewrite the passage with clearer, more polished prose. Keep the meaning and roughly the same length.',
    shorter:    'Rewrite the passage to be shorter and more concise. Aim for at most 60% of the original length without losing key facts.',
    more_formal: 'Rewrite the passage in a more formal, professional register suitable for institutional clients.',
    more_casual: 'Rewrite the passage in a friendlier, more conversational register suitable for retail clients.',
    simplify:   'Rewrite the passage in plainer English that a reader without financial expertise can understand. Replace jargon with everyday terms.'
  };

  if (!Object.prototype.hasOwnProperty.call(actionInstructions, action)) {
    throw createValidationError(
      `Unknown rewrite action: "${action}". Expected one of: ${Object.keys(actionInstructions).join(', ')}.`
    );
  }

  const tone = style?.tone || 'professional';
  const formality = style?.formality || 'balanced';

  // Written for current Claude models: state the constraints once, at normal
  // volume, with the reason. Promissory terms are described rather than
  // named so the prompt itself stays clean when compliance scanners read it.
  const systemPrompt =
    `You are an editor for a registered investment advisor's newsletter. ` +
    `Match this tone: ${tone}, formality: ${formality}. ` +
    `${actionInstructions[action]} ` +
    'Keep every number, date, percentage and dollar figure exactly as written, and do not add or change data points. ' +
    'Use measured, balanced language consistent with SEC and FINRA marketing rules: no promissory or superlative performance claims, ' +
    'and no forward-looking promises or absolute statements about future performance. ' +
    'Return only the rewritten passage, with no preamble, quotes or explanation.';

  const userPrompt = `PASSAGE:\n${trimmed}\n\nReturn only the rewritten passage.`;

  await service.initialize();

  const body = JSON.stringify({
    prompt: userPrompt,
    systemPrompt,
    mode: 'rewrite',
    maxTokens: 500
  });

  const doFetch = async (token) =>
    fetch(service.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token
      },
      body
    });

  let response;
  try {
    const token = await service.getAuthToken();
    response = await doFetch(token);

    // Issue #304 — Cognito ID tokens have a 1hr TTL. If the cached session
    // has gone stale the first request 401s; retry ONCE with a forcibly
    // refreshed token before surfacing the failure to the user.
    if (response.status === 401) {
      service.logger.warn('Rewrite got 401; retrying with refreshed auth token');
      const freshToken = await service.getAuthToken({ forceRefresh: true });
      response = await doFetch(freshToken);
    }
  } catch (error) {
    service.logger.error('Rewrite request failed:', error);
    if (error.name === 'ServiceError') throw error;
    createNetworkError('Rewrite failed', error);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // After the forceRefresh retry, a still-401 response means the session
    // truly is gone (refresh token revoked / signed out elsewhere). Give
    // the user an actionable message rather than the raw status code.
    if (response.status === 401) {
      createNetworkError('Your session has expired. Please sign in again.');
    }
    createNetworkError(
      errorData.error || errorData.message || `Rewrite failed with status ${response.status}`
    );
  }

  const data = await response.json();
  let rewritten = (data.content || data.text || '').toString().trim();

  // Strip surrounding quotation marks the model occasionally adds.
  rewritten = rewritten.replace(/^["'`""]+|["'`""]+$/g, '').trim();

  if (!rewritten) {
    createNetworkError('Rewrite returned empty content');
  }

  return rewritten;
}

/**
 * "Apply My Style" — rewrite an entire piece of content to match a saved
 * writing-style profile while preserving every fact, number, and date.
 *
 * Powers the Apply My Style action and (via the Lambda rewrite branch)
 * shares its endpoint with the inline AI toolbar. Service-bound: uses the
 * caller's apiEndpoint + auth + logger + budget + usage-stats plumbing.
 *
 * @param {Object} service - NewsletterAIService instance
 * @param {string} content - Original content to transform
 * @param {Object} styleProfile - Style profile (writingStyle/tone/complexity,
 *   optionally with 9-dim CommunicationStyle fields like formality, personalTouch)
 * @param {Object} [options] - { preserveFormatting, maxLength }
 * @returns {Promise<{ content: string, tokensUsed: number, estimatedCost: number, metadata: Object }>}
 */
export async function regenerateWithStyle(service, content, styleProfile, options = {}) {
  await service._assertWithinDailyBudget();
  service.logger.info('🎨 regenerateWithStyle called:', {
    contentLength: content?.length,
    styleName: styleProfile?.name,
    writingStyle: styleProfile?.writingStyle,
    tone: styleProfile?.tone,
    complexity: styleProfile?.complexity
  });

  await service.initialize();

  if (!content || !styleProfile) {
    throw createValidationError('Content and style profile are required for regeneration');
  }

  try {
    const token = await service.getAuthToken();

    // Build style-specific prompt (delegate to pure helper in this module).
    const styleInstructions = buildStyleInstructions(styleProfile);

    // Sprint 4 #67 — If the caller passed a 9-dimension CommunicationStyle
    // (e.g. tone+formality+personalTouch+technicalLevel...), also include
    // the canonical voice profile block so this rewrite path matches the
    // same voice contract used by generateNewsletter.
    const hasNineDimShape =
      styleProfile && (
        styleProfile.formality !== undefined ||
        styleProfile.personalTouch !== undefined ||
        styleProfile.confidence !== undefined ||
        styleProfile.technicalLevel !== undefined ||
        styleProfile.sentenceComplexity !== undefined
      );
    const communicationStyleBlock = hasNineDimShape
      ? service._buildCommunicationStyleBlock(styleProfile)
      : '';

    // Create the regeneration prompt
    const systemPrompt = `${communicationStyleBlock}You are a professional financial communication expert. Rewrite the provided content to match a specific writing style while preserving all factual information, numbers, and key points.

${styleInstructions}

Keep every number, percentage, date and fact exactly as provided, because the advisor is accountable for each figure. Keep the overall structure and flow, add no new information, and drop no important detail; change only the style, tone and word choice. ${options.preserveFormatting !== false ? 'Preserve markdown formatting (headers, lists, bold, italic).' : 'You may adjust formatting as needed.'} Keep the content approximately the same length${options.maxLength ? ` (max ${options.maxLength} words)` : ''}.

The goal is to change the style and tone while keeping the substance identical.`;

    const requestBody = {
      // The Lambda's rewrite branch (lambda-fix/index.js) reads `prompt`
      // (extracted as `directPrompt`). Earlier callers of this method
      // sent `templateContent` instead, which works for the legacy
      // full-newsletter path but fails the rewrite-branch validator with
      // `"prompt is required for rewrite mode"`. We send both so either
      // contract is satisfied — Apply My Style + InlineAIToolbar paths
      // converge on the same Lambda branch.
      prompt: content,
      templateContent: content,
      systemPrompt,
      style: styleProfile.writingStyle || 'formal',
      tone: styleProfile.tone || 'professional',
      complexity: styleProfile.complexity || 'intermediate',
      mode: 'rewrite',
      // Add minimal clientData to satisfy Lambda requirement
      // For style regeneration, we don't have actual client data
      clientData: {
        name: 'Style Regeneration',
        type: 'rewrite'
      },
      writingPreferences: {
        formality: styleProfile.writingStyle === 'formal' ? 'formal' :
                   styleProfile.writingStyle === 'conversational' ? 'casual' : 'neutral',
        tone: styleProfile.tone,
        technicalLevel: styleProfile.complexity,
        sentenceLength: styleProfile.sentenceLength || 'medium',
        vocabularyLevel: styleProfile.vocabularyLevel || 'standard'
      }
    };

    service.logger.info('📤 Sending regeneration request to AI API...');

    const response = await fetch(service.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      service.logger.error('AI API error:', { status: response.status, error: errorText });
      throw createNetworkError(`AI regeneration failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    // DEBUG: Log the actual AI response structure
    console.log('[NewsletterAIService] AI Response structure:', {
      hasContent: !!data.content,
      hasNewsletter: !!data.newsletter,
      hasText: !!data.text,
      contentLength: data.content?.length || data.newsletter?.length || data.text?.length || 0,
      tokensUsed: data.tokensUsed || data.usage?.total_tokens || 0,
      responseKeys: Object.keys(data)
    });

    service.logger.info('✅ AI regeneration successful');

    // Extract content from response
    const regeneratedContent = data.content || data.newsletter || data.text || '';

    // DEBUG: Log content extraction
    console.log('[NewsletterAIService] Extracted content length:', regeneratedContent.length);
    console.log('[NewsletterAIService] Content preview:', regeneratedContent.substring(0, 200));

    // Extract usage metadata
    const metadata = {
      tokensUsed: data.tokensUsed || data.usage?.total_tokens || 0,
      estimatedCost: data.estimatedCost || 0,
      model: data.model || 'gpt-4'
    };

    // Update usage stats
    await service.updateUsageStats(metadata);

    return {
      content: regeneratedContent,
      tokensUsed: metadata.tokensUsed,
      estimatedCost: metadata.estimatedCost,
      metadata
    };
  } catch (error) {
    service.logger.error('Error in regenerateWithStyle:', error);

    if (error.name === 'ServiceError') {
      throw error;
    }

    throw createNetworkError('Failed to regenerate content with AI', error);
  }
}

/**
 * Regenerate a single named section of the newsletter (Sprint 4 #66).
 *
 * Cost-optimized: pulls fresh AI-generated placeholder text from S3
 * (Rule 17 — fetchLatestPlaceholders, NOT static samples), assembles a
 * pre-filled section template, and asks the LLM to ONLY format/polish it
 * for the requested tone. The LLM does NOT generate the underlying
 * content — that already came from the daily Lambda batch.
 *
 * Caches results on the service instance (service._sectionRegenCache) so
 * a profile/tone change busts the cache cleanly.
 *
 * @param {Object} service - NewsletterAIService instance
 * @param {Object} params
 * @param {string} params.sectionKey - placeholder prefix (e.g. 'market_overview')
 * @param {Object} [params.marketData] - market data hint passed to the placeholder fetch
 * @param {Object} [params.userProfile] - advisor profile (for personalization)
 * @param {string} [params.tone='professional'] - writing tone for the polish step
 * @param {string} [params.currentContent] - existing section markdown (structural hint)
 * @param {boolean} [params.forceFresh=false] - bypass the in-memory cache
 * @param {boolean} [params.useStyleProfile=true] - apply advisor's voice profile
 * @returns {Promise<{ content: string, metadata: Object, error?: string }>}
 */
export async function regenerateSection(service, params = {}) {
  await service._assertWithinDailyBudget();
  const {
    sectionKey,
    marketData = {},
    userProfile = {},
    tone = 'professional',
    currentContent = '',
    forceFresh = false,
    useStyleProfile = true,
    // #170 — optional EditorialBrief lookup args. Both must be present for
    // the brief integration to fire; absent values fall through to the
    // pre-#170 prompt path.
    seriesId = null,
    campaignId = null,
  } = params;

  if (!sectionKey || typeof sectionKey !== 'string') {
    throw createValidationError('sectionKey is required for regenerateSection()');
  }

  // Sprint 5 #106 — fetch the advisor's voice profile so we can inject it
  // into the section's polish prompt. Done early so the result feeds both
  // the cache key (so a profile change busts cached output) and the
  // returned metadata.
  let communicationStyle = null;
  let communicationStyleApplied = false;
  let communicationStyleSource = 'none';
  if (useStyleProfile) {
    communicationStyle = await service._getAdvisorCommunicationStyle();
    communicationStyleApplied = true;
    communicationStyleSource = communicationStyle ? 'advisor_profile' : 'system_default';
  }

  const today = new Date().toISOString().slice(0, 10);
  const cacheKey = `${sectionKey}|${tone}|${communicationStyleSource}|${today}`;

  if (!forceFresh && service._sectionRegenCache.has(cacheKey)) {
    service.logger.info(`[regenerateSection] cache hit for ${cacheKey}`);
    return service._sectionRegenCache.get(cacheKey);
  }

  // Pull REAL S3 placeholders (Rule 17). On any failure, gracefully degrade.
  let allPlaceholders = {};
  try {
    const placeholderData = await lambdaPlaceholderService.fetchLatestPlaceholders(
      'latest',
      marketData || {}
    );
    if (placeholderData?.placeholders) {
      allPlaceholders = placeholderData.placeholders;
    } else if (placeholderData?.contents?.placeholders) {
      allPlaceholders = placeholderData.contents.placeholders;
    }
  } catch (fetchErr) {
    service.logger.warn('[regenerateSection] failed to fetch S3 placeholders:', fetchErr);
  }

  // Filter placeholders that belong to this section (e.g. 'market_overview.*').
  const prefix = `${sectionKey}.`;
  const sectionPlaceholders = Object.entries(allPlaceholders).filter(
    ([k]) => k === sectionKey || k.startsWith(prefix)
  );

  // Assemble a pre-filled section block from the placeholders.
  const sectionTitle = sectionKey
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  let preFilledSection;
  if (sectionPlaceholders.length === 0) {
    // No matching placeholders — fall back to the existing content (or a notice).
    preFilledSection = currentContent
      ? `## ${sectionTitle}\n\n${currentContent}`
      : `## ${sectionTitle}\n\n_No data available for this section right now._`;
  } else {
    const body = sectionPlaceholders
      .map(([, value]) => String(value || '').trim())
      .filter(Boolean)
      .join('\n\n');
    preFilledSection = `## ${sectionTitle}\n\n${body}`;
  }

  // System prompt — compliance-clean, format-only.
  // NOTE: We deliberately do NOT mention promotional or promissory terms by name,
  // even in negation, so the prompt itself remains compliant when scanned.
  const guardrails =
    `You are formatting a single newsletter section for a registered investment advisor. ` +
    `The factual content has already been written; polish the prose to match a "${tone}" tone ` +
    `while keeping every number, date, and percentage exactly as given. ` +
    `Add no new facts, predictions, or recommendations, and make no forward-looking promises ` +
    `or absolute statements about future performance. ` +
    `Use measured, balanced language consistent with SEC and FINRA marketing guidance. ` +
    `Keep the leading "## ${sectionTitle}" heading and return only the section markdown, with no preamble or explanation.`;

  // Sprint 5 #106 — prepend the advisor's voice block so the polish step
  // produces output that matches their saved voice, not generic prose.
  // Compliance guardrails come AFTER the voice block, so any conflict
  // resolves toward compliance.
  const voiceBlock = useStyleProfile
    ? service._buildCommunicationStyleBlock(communicationStyle)
    : '';

  // #170 / #313 — EditorialBrief integration. When the feature flag is on
  // AND the section is one of BRIEF_AWARE_SECTIONS AND an approved brief
  // exists for this series, inject the thesis as an additional prompt
  // block. Sits BETWEEN voice and guardrails so the compliance rules
  // still win on any conflict. Brief lookup failure must not block prose
  // generation — degrade gracefully to the existing path.
  //
  // #313 multi-section unlock: the alpha-proof gate on `chart_of_the_week`
  // was the smallest possible test of the plumbing. Now that briefs can
  // carry per-section thesis text, all signature sections consult the
  // brief.
  let editorialBriefBlock = '';
  if (editorialBriefService.isEnabled() && BRIEF_AWARE_SECTIONS.includes(sectionKey)) {
    try {
      const brief = await editorialBriefService.getActiveBriefForCampaign({
        seriesId,
        campaignId,
      });
      const thesis = editorialBriefService.getThesisForSection(brief, sectionKey);
      if (thesis) {
        editorialBriefBlock = `EDITORIAL BRIEF (approved thesis):\n${thesis}`;
        console.log('[EditorialBrief] applied', {
          campaignId,
          sectionKey,
          briefId: brief.id,
        });
      }
    } catch (briefErr) {
      service.logger.warn('[EditorialBrief] lookup failed:', briefErr);
      // Fall through — generation proceeds without the brief block.
    }
  }

  const systemPrompt = [voiceBlock, editorialBriefBlock, guardrails]
    .filter(Boolean)
    .join('\n\n');

  let llmResult;
  let llmError;

  try {
    const token = await service.getAuthToken();
    const response = await fetch(service.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({
        templateContent: preFilledSection,
        systemPrompt,
        mode: 'section_regen',
        style: tone,
        tone,
        sectionKey,
        // Lambda requires clientData to be present.
        clientData: { name: userProfile?.name || 'Advisor', type: 'section_regen' },
        writingPreferences: {
          tone,
          formality: tone === 'conversational' ? 'casual' : 'neutral',
        },
      }),
    });

    if (!response.ok) {
      const errBody = await response.text().catch(() => '');
      throw new Error(`AI section regen failed: ${response.status} ${errBody}`);
    }

    const data = await response.json();
    const polished = data?.content || data?.newsletter || data?.text || '';
    llmResult = polished || preFilledSection; // fall back to pre-filled if LLM returned nothing

    // Track usage (best-effort).
    try {
      const meta = data?.metadata || {
        tokensUsed: { total: data?.tokensUsed || 0 },
        estimatedCost: { total: data?.estimatedCost || 0 },
      };
      await service.updateUsageStats(meta);
    } catch (statsErr) {
      service.logger.warn('[regenerateSection] usage stats update failed:', statsErr);
    }
  } catch (err) {
    service.logger.error(`[regenerateSection] LLM polish failed for ${sectionKey}:`, err);
    llmResult = preFilledSection; // graceful degradation: return raw pre-filled section
    llmError = err.message || String(err);
  }

  const result = {
    content: llmResult,
    metadata: {
      sectionKey,
      tone,
      placeholderCount: sectionPlaceholders.length,
      regeneratedAt: new Date().toISOString(),
      usedFallback: !!llmError,
      // Sprint 5 #106 — surface style application status to callers.
      communicationStyleApplied,
      communicationStyleSource,
    },
  };
  if (llmError) result.error = llmError;

  if (!forceFresh) service._sectionRegenCache.set(cacheKey, result);
  return result;
}
